import { dealerDossierDetailAnswerReadSchema } from "@hakili/contracts";
import { t } from "@hakili/i18n";

/**
 * `P1-DLW-052` (B5-b): **`readDealerDossierDetail`, the one place dealer-web reads `GET /api/v1/me/dealership/dossiers/{id}`** (right `gererInventaire`, the dealership's own dossiers; another's answers what an unknown id answers, a 404, in the door's own words).
 *
 * The answer is parsed with the contract's READ twin (extra keys tolerated at every level, every required field enforced) and never drawn from half of it. The offer's four terms (Montant financé, Durée, Taux nominal, Mensualité) are `offer.terms` since `DEALER-OFFER-TERMS` (#927): the client's own shape and source, REQUIRED on a retained offer, so an offer served without them, or with half of them, is an unexpected answer. Nothing is derived from `offer.amountVehicle` (labelled Montant financé pour le véhicule).
 */
export type DealerDossierDetail = ReturnType<typeof dealerDossierDetailAnswerReadSchema.parse>;

export type DealerDossierDetailRead =
  | { readonly ok: true; readonly value: DealerDossierDetail }
  | { readonly ok: false; readonly message: string };

export interface DealerDossierDetailSeam {
  readonly request: <T>(
    path: "/api/v1/me/dealership/dossiers/{id}",
    method: "get",
    options: { readonly params: { readonly id: string } },
  ) => Promise<T>;
}

export async function readDealerDossierDetail(
  api: DealerDossierDetailSeam,
  dossierId: string,
): Promise<DealerDossierDetailRead> {
  try {
    const answer = await api.request<unknown>("/api/v1/me/dealership/dossiers/{id}", "get", {
      params: { id: dossierId },
    });
    const parsed = dealerDossierDetailAnswerReadSchema.safeParse(answer);
    return parsed.success
      ? { ok: true, value: parsed.data }
      : { ok: false, message: t("common.error.reponseInattendue") };
  } catch (failure) {
    // A `TypeError` is the browser's own English « Failed to fetch »: it falls to the sentence a French screen can say.
    const message = (failure as { message?: unknown } | null)?.message;
    return {
      ok: false,
      message:
        failure instanceof TypeError || typeof message !== "string" || message === ""
          ? t("common.error.connexionEchouee")
          : message,
    };
  }
}
